import Button from '@rio-cloud/rio-uikit/Button';
import { FormattedMessage } from 'react-intl';
import { useAppSelector } from '../../../../../configuration/setup/hooks';
import { getServiceOrderBulkSelection } from '../../../reducers/serviceOrdersSlice';
import { useState } from 'react';
import { ServiceOrderBulkUpdateDialog } from './ServiceOrderBulkUpdateDialog';
import { usePostServiceOrderBulkUpdatePreviewMutation } from '../../../api/serviceOrdersApi';
import { runInBackground } from '../../../../../configuration/setup/backgroundActions';
import { handleQueryError } from '../../../notifications/ErrorNotification';
import { ServiceOrderBulkUpdatePreviewResult } from '../serviceOrder.types';
import { SERVICE_ORDER_MAX_SELECTION_SIZE } from './ServiceOrderBulkSelectionDropdown';
import { BulkUpdateSizeExceededErrorResponseCodec } from '../../../api/types/serviceOrder/errorCodes';

type ServiceOrderBulkUpdateState = {
    readonly showDialog: boolean;
    readonly previewResult?: ServiceOrderBulkUpdatePreviewResult;
};

const initialServiceOrderBulkUpdateState: ServiceOrderBulkUpdateState = {
    showDialog: false,
    previewResult: undefined,
};

export const ServiceOrderBulkUpdate = () => {
    const selectedIds = useAppSelector(getServiceOrderBulkSelection);
    const [postServiceOrderBulkUpdatePreview, serviceOrderBulkUpdatePreviewRequest] =
        usePostServiceOrderBulkUpdatePreviewMutation();

    const [bulkUpdateState, setBulkUpdateState] = useState(initialServiceOrderBulkUpdateState);

    const maxBulkSelectionExceeded = selectedIds.length > SERVICE_ORDER_MAX_SELECTION_SIZE;
    const isLoading = serviceOrderBulkUpdatePreviewRequest.isLoading;

    const openDialog = () => {
        if (selectedIds.length === 0) {
            return;
        }

        if (maxBulkSelectionExceeded) {
            setBulkUpdateState(() => ({
                showDialog: true,
                previewResult: { success: false, errorCode: 'size-exceeded' },
            }));
            return;
        }

        runInBackground(async () => {
            try {
                const updatePreview = await postServiceOrderBulkUpdatePreview({
                    orderIds: selectedIds,
                }).unwrap();
                setBulkUpdateState(() => ({
                    showDialog: true,
                    previewResult: { success: true, ...updatePreview },
                }));
            } catch (error) {
                if (BulkUpdateSizeExceededErrorResponseCodec.is(error)) {
                    setBulkUpdateState(() => ({
                        showDialog: true,
                        previewResult: { success: false, errorCode: 'size-exceeded' },
                    }));
                } else {
                    handleQueryError(error);
                }
            }
        });
    };

    const closeDialog = () => setBulkUpdateState({ ...initialServiceOrderBulkUpdateState });

    return (
        <>
            <Button
                disabled={isLoading || selectedIds.length === 0}
                bsStyle={Button.PRIMARY}
                className={isLoading ? 'btn-loading' : ''}
                iconName={isLoading ? '' : 'rioglyph-cards-add'}
                onClick={openDialog}
            >
                <FormattedMessage
                    id="outboundOrderBook.serviceOrder.toolbar.bulkUpdate"
                    values={{ count: selectedIds.length }}
                />
            </Button>
            {bulkUpdateState.showDialog && bulkUpdateState.previewResult && (
                <ServiceOrderBulkUpdateDialog previewResult={bulkUpdateState.previewResult} onClose={closeDialog} />
            )}
        </>
    );
};
